import * as React from "react";
import { Lock } from "lucide-react";
import { Switch } from "../../../ui/components/Switch";
import type { PermissionActionView } from "../../lib/permission-groups";
import * as styles from "./PermissionGroup.css";

function PermissionActionRow({
  action,
  disabled = false,
  onActionToggle,
}: {
  action: PermissionActionView;
  disabled?: boolean;
  onActionToggle?: (
    permissionId: string,
    permissionName: string,
    enabled: boolean,
  ) => void;
}) {
  const rowDisabled = disabled || action.switchDisabled || !onActionToggle;
  const showRoleLock =
    action.switchDisabled && Boolean(action.disabledReason);

  return (
    <li className={styles.actionRow}>
      <div className={styles.actionInfo}>
        <div className={styles.actionLabelRow}>
          <p className={styles.actionLabel}>{action.label}</p>
          <span
            className={styles.permissionSlug}
            title={action.permissionName}
          >
            {action.permissionName}
          </span>
        </div>
      </div>
      <div className={styles.actionControl}>
        <div
          className={styles.switchWrapper}
          title={showRoleLock ? action.disabledReason : undefined}
        >
          <Switch
            label={action.label}
            hideLabel
            checked={action.enabled}
            disabled={rowDisabled}
            onCheckedChange={(enabled) => {
              if (enabled !== action.enabled) {
                onActionToggle?.(
                  action.permissionId,
                  action.permissionName,
                  enabled,
                );
              }
            }}
          />
          {showRoleLock && (
            <span className={styles.lockOverlay} aria-hidden>
              <Lock size={14} />
            </span>
          )}
        </div>
      </div>
    </li>
  );
}

export function PermissionGroup({
  resourceLabel,
  actions,
  onActionToggle,
  disabled = false,
}: {
  resourceLabel: string;
  actions: PermissionActionView[];
  onActionToggle?: (
    permissionId: string,
    permissionName: string,
    enabled: boolean,
  ) => void;
  disabled?: boolean;
}) {
  if (actions.length === 0) return null;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h5 className={styles.resourceTitle}>{resourceLabel}</h5>
      </div>
      <ul className={styles.actionList}>
        {actions.map((action) => (
          <PermissionActionRow
            key={action.permissionName}
            action={action}
            disabled={disabled}
            onActionToggle={onActionToggle}
          />
        ))}
      </ul>
    </div>
  );
}

export function PermissionGroupGrid({
  groups,
  onActionToggle,
  disabled = false,
}: {
  groups: Array<{
    resourceKey: string;
    resourceLabel: string;
    actions: PermissionActionView[];
  }>;
  onActionToggle?: (
    permissionId: string,
    permissionName: string,
    enabled: boolean,
  ) => void;
  disabled?: boolean;
}) {
  if (groups.length === 0) return null;
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
        gap: 12,
      }}
    >
      {groups.map((group) => (
        <PermissionGroup
          key={group.resourceKey}
          resourceLabel={group.resourceLabel}
          actions={group.actions}
          onActionToggle={onActionToggle}
          disabled={disabled}
        />
      ))}
    </div>
  );
}
